import { PageHeader } from 'components/page-header';
import ErrorToast from 'components/error-toast';
import { SuccessToast } from 'components/success-toast';
import { ScenarioNav } from 'components/scenario-nav/scenario-nav';
import { ScenarioMap } from 'components/scenario-map/scenario-map';
import { NotesAccordion } from './notes-accordion';
import { ContentHeader } from './content-header';
import type { ScenarioFormLayoutProps } from './types';
import styles from './scenario-form-layout.module.scss';

/**
 * Shared page shell for all scenario form pages (DRS, DPS, Tax, Royalty, etc.).
 * Provides the full layout: page header, notes accordion, map, sidebar nav,
 * content header bar with save button, and a children slot for scenario-specific content.
 * Layout-only — no form state coupling. Parent wires all values and callbacks.
 * @param {ScenarioFormLayoutProps} props - The component props
 * @return {JSX.Element} The scenario form layout
 */
export function ScenarioFormLayout({
  title,
  notes,
  mapFeatures,
  mapFocusedId,
  mapConfigAtom,
  navItems,
  navSelectedId,
  onNavSelect,
  onNavAdd,
  onNavDelete,
  navGlobalLabel,
  contentTitle,
  isDirty,
  canSave,
  isSaving,
  onSave,
  saveLabel,
  overlapError,
  saveBlockedReason,
  headerActions,
  toast,
  onToastClose,
  eventsPanel,
  children,
}: ScenarioFormLayoutProps): JSX.Element {
  return (
    <div className={styles.scenarioForm}>
      {toast?.type === 'error' && (
        <ErrorToast
          message={toast.message}
          onClose={() => {
            onToastClose?.();
            return true;
          }}
        />
      )}
      {toast?.type === 'success' && (
        <SuccessToast
          message={toast.message}
          onClose={() => {
            onToastClose?.();
            return true;
          }}
        />
      )}

      <PageHeader
        title={title}
        breadcrumbs={[]}
        actions={headerActions ?? null}
      />

      {notes && (
        <NotesAccordion
          value={notes.value}
          onChange={notes.onChange}
          placeholder={notes.placeholder}
        />
      )}

      <ScenarioMap
        features={mapFeatures}
        focusedId={mapFocusedId}
        mapConfigAtom={mapConfigAtom}
      />

      <div className={styles.mainPanel}>
        <ScenarioNav
          items={navItems}
          selectedId={navSelectedId}
          onSelect={onNavSelect}
          onAdd={onNavAdd}
          onDelete={onNavDelete}
          globalLabel={navGlobalLabel}
        />

        <div className={styles.contentPanel}>
          <ContentHeader
            title={contentTitle}
            isDirty={isDirty}
            canSave={canSave}
            isSaving={isSaving}
            onSave={onSave}
            saveLabel={saveLabel}
            overlapError={overlapError}
            saveBlockedReason={saveBlockedReason}
          />

          {children}
        </div>
      </div>

      {eventsPanel}
    </div>
  );
}
